import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { DollarSign, Plus, X, CheckCircle2, Calendar, ScrollText, Wallet } from "lucide-react";

const StatusBadge = ({ s, t }) => {
  const map = {
    requested: { cls: "sw-badge-pending", key: "loans.status_requested" },
    active: { cls: "sw-badge-gold", key: "loans.status_active" },
    paid: { cls: "sw-badge-approved", key: "loans.status_paid" },
    rejected: { cls: "sw-badge-rejected", key: "loans.status_rejected" },
  };
  const entry = map[s];
  return <span className={`sw-badge ${entry?.cls || "sw-badge-neutral"}`}>{entry ? t(entry.key) : s}</span>;
};

function NewLoanModal({ onClose, onSaved }) {
  const { t } = useI18n();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ user_id: "", amount: "", reason: "", installments: 6, desired_disbursement_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const monthly = form.amount && form.installments ? (parseFloat(form.amount) / parseInt(form.installments)).toFixed(2) : "0.00";

  useEffect(() => {
    api.get("/users").then((r) => {
      setEmployees(r.data.filter((u) => u.active !== false && u.role !== "admin"));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id) {
      alert(t("loans.error_select_employee"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/loans", { ...form, amount: parseFloat(form.amount), installments: parseInt(form.installments) });
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[560px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[20px]">{t("loans.register_title")}</h3>
            <p className="text-[12px] text-[#747474] mt-1">{t("loans.confidentiality")}</p>
          </div>
          <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="sw-label">{t("loans.employee_required")}</label>
            <select required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="sw-input" data-testid="loan-user">
              <option value="">{t("loans.select_employee")}</option>
              {employees.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="sw-label">{t("loans.amount_required")}</label>
            <input required type="number" step="100" min="100" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="sw-input" data-testid="loan-amount" />
          </div>
          <div>
            <label className="sw-label">{t("loans.installments_required")}</label>
            <input required type="number" min="1" max="24" value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} className="sw-input" data-testid="loan-installments" />
          </div>
          <div className="md:col-span-2 bg-[#e2e8f0]/40 rounded-[12px] p-3 text-[13px]">
            {t("loans.monthly_estimate")} <span className="font-semibold text-[#475569] text-[16px]">${monthly}</span>
          </div>
          <div>
            <label className="sw-label">{t("loans.disbursement_date_required")}</label>
            <input required type="date" value={form.desired_disbursement_date} onChange={(e) => setForm({ ...form, desired_disbursement_date: e.target.value })} className="sw-input" data-testid="loan-disbursement-date" />
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">{t("loans.reason_required")}</label>
            <textarea required rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="sw-input" data-testid="loan-reason" />
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">{t("loans.notes_label")}</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="submit-loan-btn" type="submit" disabled={saving} className="sw-btn-primary">{saving ? t("loans.saving") : t("loans.register_btn")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LoanDetailModal({ loan, user, onClose, onUpdated }) {
  const { t } = useI18n();
  const [working, setWorking] = useState(false);
  const [comment, setComment] = useState("");
  const currentRole = loan.required_roles?.[loan.current_step];
  const canDecide = loan.status === "requested" && currentRole === user?.role;
  const canMarkPayment = loan.status === "active" && ["admin", "hr", "ceo"].includes(user?.role);

  const decide = async (decision) => {
    setWorking(true);
    try {
      await api.post(`/loans/${loan.id}/decide`, { decision, comment });
      await onUpdated();
    } catch (err) { alert(err?.response?.data?.detail || t("common.error_generic")); } finally { setWorking(false); }
  };

  const markPaid = async (num) => {
    setWorking(true);
    try {
      await api.post(`/loans/${loan.id}/mark-payment`, { installment_num: num });
      await onUpdated();
    } catch (err) { alert(err?.response?.data?.detail || t("common.error_generic")); } finally { setWorking(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[820px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{t("loans.label_singular")} · {loan.user_name}</div>
            <h3 className="sw-heading text-[22px] mt-1">${loan.amount?.toFixed(2)} · {loan.installments}× ${loan.monthly_amount?.toFixed(2)}</h3>
            <div className="text-[13px] text-[#747474] italic mt-1">&ldquo;{loan.reason}&rdquo;</div>
          </div>
          <div className="flex items-center gap-2"><StatusBadge s={loan.status} t={t} /><button onClick={onClose} className="text-[#747474]"><X size={20} /></button></div>
        </div>

        {loan.agreement && (
          <div className="sw-card !p-4 mb-4 bg-[#e2e8f0]/30">
            <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-2"><ScrollText size={11} className="inline" /> {t("loans.agreement")} · {loan.agreement.reference}</div>
            <div className="text-[12px] text-[#323232] leading-relaxed">{loan.agreement.terms}</div>
            <div className="text-[11px] text-[#747474] mt-2">{t("loans.approved_by")} {loan.agreement.approved_by?.map((a) => `${a.name} (${a.role})`).join(", ")}</div>
          </div>
        )}

        <div className="sw-card !p-4 mb-4">
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3">{t("loans.approval_chain")}</div>
          <div className="flex flex-wrap gap-2 items-center">
            {loan.required_roles?.map((r, i) => {
              const ap = loan.approvals?.find((a) => a.step === i);
              const done = !!ap;
              const isCurrent = i === loan.current_step && loan.status === "requested";
              // Show translated role label ("RH" instead of "hr", "Gestor" instead
              // of "manager") so integrantes understand who's holding the request.
              const roleKey = `admin.role_${r}`;
              const translated = t(roleKey);
              const roleLabel = translated === roleKey ? r : translated;
              return (
                <div key={i} data-testid={`loan-chain-step-${i}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-[10px] text-[12px] ${done ? (ap.decision === "approved" ? "bg-[#b8e6cd] text-[#0b430b]" : "bg-[#e5c1c1] text-[#881a27]") : isCurrent ? "bg-[#fef3c7] text-[#92400e]" : "bg-[#f6f6f6] text-[#747474]"}`}>
                  <span className="font-medium">{roleLabel}</span>
                  {done && <span className="text-[10px]">· {ap.user_name}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {loan.payment_schedule?.length > 0 && (
          <div className="sw-card !p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider text-[#475569]"><Wallet size={11} className="inline" /> {t("loans.payment_schedule")}</div>
              <div className="text-[12px]"><span className="text-[#747474]">{t("loans.paid_label")}</span> <span className="font-semibold text-[#008932]">${loan.paid_amount?.toFixed(2)}</span> · <span className="text-[#747474]">{t("loans.outstanding_label")}</span> <span className="font-semibold text-[#881a27]">${loan.outstanding_amount?.toFixed(2)}</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {loan.payment_schedule.map((p) => (
                <div key={p.installment_num} className={`flex items-center gap-3 p-2 rounded-[10px] ${p.status === "paid" ? "bg-[#b8e6cd]/40" : "bg-[#f6f6f6]"}`}>
                  {p.status === "paid" ? <CheckCircle2 size={16} className="text-[#008932]" /> : <Calendar size={16} className="text-[#747474]" />}
                  <div className="flex-1">
                    <div className="text-[13px] font-medium">#{p.installment_num} · ${p.amount?.toFixed(2)}</div>
                    <div className="text-[11px] text-[#747474]">{t("loans.due")} {p.due_date}{p.paid_at && ` · ${t("loans.paid_on")} ${p.paid_at?.slice(0,10)}`}</div>
                  </div>
                  {p.status === "pending" && canMarkPayment && (
                    <button data-testid={`mark-paid-${p.installment_num}`} onClick={() => markPaid(p.installment_num)} disabled={working} className="sw-btn-secondary !px-3 !py-1 !text-[11px]">{t("loans.mark_paid")}</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {canDecide && (
          <div className="sw-card !p-4 mb-4 bg-[#fef3c7] border-[#f59e0b]">
            <div className="font-medium text-[13px] mb-2 text-[#92400e]">{t("loans.your_approval", { role: currentRole })}</div>
            <input placeholder={t("loans.comment_optional")} value={comment} onChange={(e) => setComment(e.target.value)} className="sw-input mb-2" />
            <div className="flex justify-end gap-2">
              <button data-testid="loan-reject-btn" onClick={() => decide("rejected")} disabled={working} className="sw-btn-secondary !text-[#881a27]">{t("loans.reject")}</button>
              <button data-testid="loan-approve-btn" onClick={() => decide("approved")} disabled={working} className="sw-btn-primary !bg-[#008932] hover:!bg-[#0b430b]">{t("loans.approve")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Loans() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [scope, setScope] = useState(user?.role === "employee" ? "mine" : "all");

  const load = async () => {
    const params = new URLSearchParams();
    if (filterStatus !== "all") params.set("status", filterStatus);
    if (scope === "mine") params.set("mine", "true");
    const r = await api.get(`/loans?${params.toString()}`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [filterStatus, scope]); // eslint-disable-line

  const isStaff = ["manager", "hr", "ceo", "admin"].includes(user?.role);

  const statusLabel = (s) => s === "all" ? t("loans.all") : t(`loans.status_${s}`);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><DollarSign size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("loans.title")}</h1>
            <p className="text-[14px] text-[#747474]">{items.length} {t("loans.count_one")} · {items.filter((i) => i.status === "active").length} {t("loans.active")}</p>
          </div>
        </div>
        {["hr", "admin"].includes(user?.role) && <button data-testid="new-loan-btn" onClick={() => setShowNew(true)} className="sw-btn-primary"><Plus size={14} /> {t("loans.register_btn")}</button>}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {isStaff && (
          <div className="flex gap-1 mr-2">
            <button onClick={() => setScope("mine")} className={`px-3 py-1.5 rounded-[10px] text-[12px] ${scope === "mine" ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{t("loans.mine")}</button>
            <button onClick={() => setScope("all")} className={`px-3 py-1.5 rounded-[10px] text-[12px] ${scope === "all" ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{t("loans.all")}</button>
          </div>
        )}
        {["all", "requested", "active", "paid", "rejected"].map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-[10px] text-[11px] font-medium ${filterStatus === s ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>{statusLabel(s)}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="sw-card text-center py-12 text-[#747474]">{t("loans.no_loans")}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((l) => {
            const progress = l.amount > 0 ? Math.round((l.paid_amount / l.amount) * 100) : 0;
            return (
              <button key={l.id} data-testid={`loan-card-${l.id}`} onClick={() => setDetail(l)} className="sw-card text-left hover:border-[#475569]">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#475569]">{l.user_name}</div>
                    <div className="font-medium text-[18px] text-[#323232] mt-1">${l.amount?.toFixed(2)} · {l.installments}× ${l.monthly_amount?.toFixed(2)}</div>
                    <div className="text-[12px] text-[#747474] italic mt-1 line-clamp-1">&ldquo;{l.reason}&rdquo;</div>
                  </div>
                  <StatusBadge s={l.status} t={t} />
                </div>
                {l.status === "requested" && l.required_roles?.[l.current_step] && (() => {
                  const r = l.required_roles[l.current_step];
                  const roleKey = `admin.role_${r}`;
                  const translated = t(roleKey);
                  const label = translated === roleKey ? r : translated;
                  return (
                    <div data-testid="loan-pending-approver" className="text-[10px] text-[#92400e] mb-2">
                      {t("travel.waiting_for")} <strong>{label}</strong>
                    </div>
                  );
                })()}
                {l.status === "active" && (
                  <div className="mt-3">
                    <div className="flex justify-between text-[11px] text-[#747474] mb-1"><span>{t("loans.repaid_pct")} {progress}%</span><span>${l.paid_amount?.toFixed(2)} / ${l.amount?.toFixed(2)}</span></div>
                    <div className="h-2 bg-[#f6f6f6] rounded-full overflow-hidden"><div className="h-full bg-[#475569] transition-all" style={{ width: `${progress}%` }} /></div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {showNew && <NewLoanModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {detail && (
        <LoanDetailModal
          loan={detail}
          user={user}
          onClose={() => setDetail(null)}
          onUpdated={async () => {
            await load();
            const fresh = (await api.get(`/loans/${detail.id}`)).data;
            setDetail(fresh);
          }}
        />
      )}
    </div>
  );
}
